import React, { useState, useEffect, useRef } from 'react';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import StatsSection from './StatsSection';
import Styles from './Dashboard.module.scss';
import ActiveCampaigns from './ActiveCampaigns';
import {
  FiCalendar,
  FiFile,
  FiFileText,
  FiLayers,
  FiChevronDown,
  FiPlus,
  FiX,
  FiSend,
  FiUpload,
} from 'react-icons/fi';
import BriefSection from './BriefSection';
import { AiOutlineProject } from 'react-icons/ai';
import NewDocumentModal from 'Components/Modals/NewDocumentModal/NewDocumentModal';
import BriefDrawer from 'Components/Drawers/BriefDrawer/BriefDrawer';
import { LuPlus } from 'react-icons/lu';
import useCreateBrief from 'hooks/briefs/useCreateBrief';
import useCreateProject from 'hooks/projects/useCreateProject';
import { useSelector, useDispatch } from 'react-redux';
import onBoardingService from 'services/onBoardingService';
import { actions } from 'redux/slices/auth';
import { formattedDate } from 'helpers';
import DocumentSection from './DocumentSection';
import useTotalCountDocuments from 'hooks/documents/useTotalCountDocuments';
import useOverdueCountBriefs from 'hooks/briefs/useOverdueCountBriefs';
import UpgradeModal from 'Containers/Modal/UpgradeModal/UpgradeModal';
import Announcement from 'Components/Pages/Brand/Announcement';
import { useNavigate } from 'react-router-dom';
import {
  success as renderSuccess,
  error as renderError,
} from 'helpers/toaster.js';
import { generateCampaignTitleWithClaude } from 'services/anthropicService';
import CampaignErrorModal from 'Components/Modal/CampaignErrorModal/CampaignErrorModal';
import { getRequest } from 'helpers/axios';
import { extractSupportingDocuments } from 'services/projectService';

// Import icons for channel grid
import {
  FaInstagram,
  FaYoutube,
  FaHeadphones,
  FaEnvelope,
  FaFacebook,
  FaBlog,
  FaLinkedin,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { SiTiktok } from 'react-icons/si';

// Campaign goal options
const CAMPAIGN_GOALS = [
  { label: 'Brand Awareness', value: 'Brand Awareness' },
  { label: 'Engagement', value: 'Engagement' },
  { label: 'Lead Generation', value: 'Lead Generation' },
  { label: 'Sales Conversion', value: 'Sales Conversion' },
];

// Add comprehensive language options
const LANGUAGE_OPTIONS = [
  { label: 'English (US)', value: 'en-US' },
  { label: 'English (UK)', value: 'en-GB' },
  { label: 'Spanish', value: 'es' },
  { label: 'French', value: 'fr' },
  { label: 'German', value: 'de' },
  { label: 'Italian', value: 'it' },
  { label: 'Portuguese', value: 'pt' },
  { label: 'Dutch', value: 'nl' },
  { label: 'Russian', value: 'ru' },
  { label: 'Japanese', value: 'ja' },
  { label: 'Chinese (Simplified)', value: 'zh-CN' },
  { label: 'Chinese (Traditional)', value: 'zh-TW' },
  { label: 'Arabic', value: 'ar' },
  { label: 'Bengali', value: 'bn' },
  { label: 'Bulgarian', value: 'bg' },
  { label: 'Catalan', value: 'ca' },
  { label: 'Croatian', value: 'hr' },
  { label: 'Czech', value: 'cs' },
  { label: 'Danish', value: 'da' },
  { label: 'Estonian', value: 'et' },
  { label: 'Finnish', value: 'fi' },
  { label: 'Greek', value: 'el' },
  { label: 'Hebrew', value: 'he' },
  { label: 'Hindi', value: 'hi' },
  { label: 'Hungarian', value: 'hu' },
  { label: 'Indonesian', value: 'id' },
  { label: 'Korean', value: 'ko' },
  { label: 'Latvian', value: 'lv' },
  { label: 'Lithuanian', value: 'lt' },
  { label: 'Malay', value: 'ms' },
  { label: 'Mongolian', value: 'mn' },
  { label: 'Norwegian', value: 'no' },
  { label: 'Persian', value: 'fa' },
  { label: 'Polish', value: 'pl' },
  { label: 'Romanian', value: 'ro' },
  { label: 'Serbian', value: 'sr' },
  { label: 'Slovak', value: 'sk' },
  { label: 'Slovenian', value: 'sl' },
  { label: 'Swedish', value: 'sv' },
  { label: 'Thai', value: 'th' },
  { label: 'Turkish', value: 'tr' },
  { label: 'Ukrainian', value: 'uk' },
  { label: 'Urdu', value: 'ur' },
  { label: 'Vietnamese', value: 'vi' },
];

const Dashboard = () => {
  // Global state & hooks
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch fresh brands data on mount to ensure correct BrandProfile IDs
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await onBoardingService.getUserBrandInfo();
        dispatch(actions.updateUser({ user: { user_brands: response?.data } }));
      } catch (error) {
        console.error('Failed to fetch brands:', error);
      }
    };
    fetchBrands();
  }, [dispatch]);

  // Auto-redirect to payment if plan is stored in localStorage (only for users without active subscriptions)
  useEffect(() => {
    // Check for lifetime deal intent first
    const lifetimeDealIntent = localStorage.getItem('lifetimeDealIntent');
    if (lifetimeDealIntent) {
      localStorage.removeItem('lifetimeDealIntent');
      navigate('/payment?lifetime=true');
      return;
    }

    // Check for regular plan selection
    const storedPlan = localStorage.getItem('selectedPlan');
    if (storedPlan) {
      try {
        const planData = JSON.parse(storedPlan);

        // Check if user has ever had a subscription (new user vs returning user)
        const currentSubscription = user?.subscriptions?.current_subscription;
        const lastSubscription = user?.subscriptions?.last_subscription;

        // Check if user has an active subscription
        const hasActiveSubscription =
          currentSubscription && currentSubscription.active;

        // If user has an active subscription, just clear the stored plan and don't redirect
        if (hasActiveSubscription) {
          localStorage.removeItem('selectedPlan');
          return;
        }

        const isNewUser = !currentSubscription && !lastSubscription;

        // Only add trial=true if user is completely new (never had any subscription)
        const trialParam = isNewUser ? '?trial=true' : '';

        // Clear the stored plan data since we're using it now
        localStorage.removeItem('selectedPlan');

        // Navigate immediately to payment page with the selected plan
        navigate(`/payment${trialParam}`, {
          state: {
            subscriptionId: planData.id,
            selectedPlan: planData,
          },
        });
      } catch (error) {
        console.error('Error parsing stored plan data:', error);
        localStorage.removeItem('selectedPlan');
      }
    }
  }, [navigate, user?.subscriptions]);

  // Insights & stats state
  const [insightsDuration, setInsightsDuration] = useState('week');
  const { overdue: overdueBriefsCount, refetch: refetchBriefsCount } =
    useOverdueCountBriefs(insightsDuration);
  const {
    completed: completedDocumentsCount,
    in_progress: inProgressDocumentsCount,
    refetch: refetchDocumentStats,
  } = useTotalCountDocuments(insightsDuration);

  // Modal states for other actions (document, brief)
  const [modalView, setModalView] = useState({
    newDocument: false,
    addTask: false,
    // Note: We are no longer using addProject since the dashboard triggers creation directly.
  });

  // Changed from single selection to multiple selection with no preselection
  const [selectedChannels, setSelectedChannels] = useState([]);
  const planName = user?.subscriptions?.current_subscription?.plan_name;
  const userName = user?.name || 'User';

  // Campaign state variables
  const [campaignDescription, setCampaignDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // New state for selected brand (exactly like in ProjectDrawer)
  const [selectedBrand, setSelectedBrand] = useState('');
  const [brandLabel, setBrandLabel] = useState('Choose brand');

  // Campaign goal state
  const [campaignGoal, setCampaignGoal] = useState('');
  const [campaignGoalLabel, setCampaignGoalLabel] = useState(
    'Choose campaign goal',
  );
  const [showGoalDropdown, setShowGoalDropdown] = useState(false);

  // Add state for language
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [languageLabel, setLanguageLabel] = useState('English (US)');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // Supporting documents state
  const [supportingFiles, setSupportingFiles] = useState([]);
  const [extractedSupportingContent, setExtractedSupportingContent] =
    useState('');
  const [isExtractingContent, setIsExtractingContent] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [supportingDocsErrors, setSupportingDocsErrors] = useState([]);

  // Add refs for detecting clicks outside dropdowns
  const goalDropdownRef = useRef(null);
  const brandDropdownRef = useRef(null);
  const languageDropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  // Handle clicks outside of dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        goalDropdownRef.current &&
        !goalDropdownRef.current.contains(event.target)
      ) {
        setShowGoalDropdown(false);
      }
      if (
        brandDropdownRef.current &&
        !brandDropdownRef.current.contains(event.target)
      ) {
        setShowBrandDropdown(false);
      }
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target)
      ) {
        setShowLanguageDropdown(false);
      }
    }

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle supporting documents extraction
  useEffect(() => {
    const handleExtractSupportingContent = async () => {
      if (supportingFiles.length === 0) {
        setExtractedSupportingContent('');
        return;
      }

      setIsExtractingContent(true);
      setExtractionProgress(0);
      setSupportingDocsErrors([]);

      try {
        // Simulate progress
        const progressInterval = setInterval(() => {
          setExtractionProgress((prev) => Math.min(prev + 10, 90));
        }, 200);

        const response = await extractSupportingDocuments(supportingFiles);

        clearInterval(progressInterval);
        setExtractionProgress(100);

        if (response?.data?.data) {
          setExtractedSupportingContent(response.data.data);
          if (response.data.errors && response.data.errors.length > 0) {
            setSupportingDocsErrors(
              response.data.errors.map((e) => `${e.filename}: ${e.error}`),
            );
          }
        }
      } catch (error) {
        console.error('Error extracting content:', error);
        setSupportingDocsErrors(['Failed to extract content from files']);
      } finally {
        setIsExtractingContent(false);
      }
    };

    handleExtractSupportingContent();
  }, [supportingFiles]);

  // Function to transform brands data (copied directly from ProjectDrawer)
  function transformBrandsData(brandsData) {
    return brandsData?.map((item) => {
      const [key, value] = Object.entries(item)[0];
      if (value === null) {
        return { label: 'Unknown', value: key };
      }
      // value is an object with brand data, extract the brand_name
      return { label: value.brand_name || 'Unknown', value: key };
    });
  }

  // Get brand options using the same approach as ProjectDrawer
  const brandOptions =
    transformBrandsData(user?.user_brands?.brands_data) || [];

  // Hooks for creating brief and project
  const { mutateAsync: createBrief } = useCreateBrief({ userId: user?.id });
  const { mutateAsync: createProject, isLoading: isCreateLoading } =
    useCreateProject({
      userId: user?.id,
    });

  // New UI styles for the updated prompt box and channel grid
  const newUIStyles = {
    greetingSection: {
      textAlign: 'center',
      marginBottom: '2rem',
    },
    greetingTitle: {
      fontSize: '1.75rem',
      fontWeight: 'bold',
      marginBottom: '0.75rem',
      color: '#000',
    },
    greetingSubtitle: {
      color: '#6b7280',
      marginBottom: '2rem',
    },
    channelGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '1rem',
      justifyContent: 'center',
      marginBottom: '2.5rem',
      position: 'relative',
      zIndex: '1',
    },
    channelCard: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: '0.7rem',
      padding: '1.5rem 1rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      width: '120px',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)',
      position: 'relative',
    },
    channelCardActive: {
      // Each card gets its own background color based on the channel's color
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      border: '2px solid #000', // Adding black border for selected state
    },
    channelIcon: {
      fontSize: '1.5rem',
      marginBottom: '0.5rem',
    },
    brandDropdown: {
      position: 'relative',
      cursor: 'pointer',
    },
    brandDropdownMenu: {
      position: 'absolute',
      top: '100%',
      left: '0',
      zIndex: '10',
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      width: '100%',
      maxHeight: '200px',
      overflowY: 'auto',
      display: 'none',
      border: '1px solid #e0e0e0',
    },
    brandDropdownMenuShow: {
      display: 'block',
    },
    brandDropdownItem: {
      padding: '0.75rem 1rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    brandDropdownItemHover: {
      backgroundColor: '#f3f4f6',
    },
  };

  // Channel options for the grid with background colors for selected state
  const channelOptions = [
    {
      id: 'Instagram',
      Icon: FaInstagram,
      color: '#E1306C',
      bgColor: '#FCEEF2',
    },
    { id: 'YouTube', Icon: FaYoutube, color: '#FF0000', bgColor: '#FEE8E8' },
    { id: 'Email', Icon: FaEnvelope, color: '#4285F4', bgColor: '#EBF1FE' },
    { id: 'Facebook', Icon: FaFacebook, color: '#1877F2', bgColor: '#E9F1FD' },
    { id: 'Blog', Icon: FaBlog, color: '#2DB67D', bgColor: '#E8F5F0' },
    { id: 'LinkedIn', Icon: FaLinkedin, color: '#0077B5', bgColor: '#E5F1F8' },
    { id: 'Twitter', Icon: FaXTwitter, color: '#000000', bgColor: '#F0F0F0' },
    { id: 'TikTok', Icon: SiTiktok, color: '#000000', bgColor: '#F0F0F0' },
  ];

  // Toggle channel selection
  const toggleChannelSelection = (channelId) => {
    setSelectedChannels((prev) => {
      if (prev.includes(channelId)) {
        return prev.filter((id) => id !== channelId);
      } else {
        return [...prev, channelId];
      }
    });
  };

  // Dropdown items for bottom actions
  const dropdownItems = [
    {
      modalKey: 'newDocument',
      Icon: FiFile,
      label: 'Document',
    },
    {
      modalKey: 'addTask',
      Icon: FiFileText,
      label: 'Brief',
    },
    {
      modalKey: 'addProject',
      Icon: FiLayers,
      label: 'Campaign',
    },
  ];

  // Brand dropdown toggle state
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);

  // Handle brand selection
  const handleBrandSelect = (brand) => {
    console.log('Brand clicked:', brand);
    console.log('Brand value:', brand.value, 'Brand label:', brand.label);

    // If the same brand is clicked again, unselect it
    if (selectedBrand === brand.value) {
      setSelectedBrand('');
      setBrandLabel('Choose brand');
    } else {
      setSelectedBrand(brand.value);
      setBrandLabel(brand.label);
      console.log('Set selectedBrand to:', brand.value);
    }
    setShowBrandDropdown(false);
  };

  // Handle campaign goal selection
  const handleGoalSelect = (goal) => {
    setCampaignGoal(goal.value);
    setCampaignGoalLabel(goal.label);
    setShowGoalDropdown(false);
  };

  // Handle language selection
  const handleLanguageSelect = (language) => {
    setSelectedLanguage(language.value);
    setLanguageLabel(language.label);
    setShowLanguageDropdown(false);
  };

  // Handle file selection for supporting documents
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 5;
    const allowedTypes = ['.pdf', '.docx', '.txt', '.rtf'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    const currentCount = supportingFiles.length;
    if (currentCount + files.length > maxFiles) {
      setSupportingDocsErrors([`Maximum ${maxFiles} files allowed`]);
      return;
    }

    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(ext)) {
        errors.push(`${file.name}: Invalid type`);
      } else if (file.size > maxSize) {
        errors.push(`${file.name}: Too large (max 10MB)`);
      } else if (!supportingFiles.some((f) => f.name === file.name)) {
        validFiles.push(file);
      }
    });

    setSupportingDocsErrors(errors);
    if (validFiles.length > 0) {
      setSupportingFiles([...supportingFiles, ...validFiles]);
    }
    e.target.value = '';
  };

  // Remove a file
  const removeFile = (index) => {
    setSupportingFiles(supportingFiles.filter((_, i) => i !== index));
  };

  // Reorder brand options to put selected brand at the top
  const getOrderedBrandOptions = () => {
    if (!selectedBrand) return brandOptions;

    const selectedBrandOption = brandOptions.find(
      (brand) => brand.value === selectedBrand,
    );
    if (!selectedBrandOption) return brandOptions;

    return [
      selectedBrandOption,
      ...brandOptions.filter((brand) => brand.value !== selectedBrand),
    ];
  };

  // Function to create a fallback title if Claude API fails
  const createFallbackTitle = (brandName, goal, channels) => {
    const dateStr = new Date().toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });

    let title = '';

    if (brandName) {
      title += `${brandName} `;
    }

    title += goal ? `${goal} ` : 'Engagement ';

    if (!channels || channels.length === 0) {
      title += 'Multi-Channel Campaign';
    } else if (channels.length === 1) {
      title += `${channels[0]} Campaign`;
    } else if (channels.length === 2) {
      title += `${channels[0]} & ${channels[1]} Campaign`;
    } else {
      title += `${channels.length}-Channel Campaign`;
    }

    title += ` (${dateStr})`;

    return title;
  };

  // New state for error handling
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorLink, setErrorLink] = useState('');

  const handleGenerateCampaign = async () => {
    // Validate that either description or supporting documents are provided
    if (!campaignDescription.trim() && !extractedSupportingContent) {
      renderError('Please provide a description or upload supporting documents');
      return;
    }

    try {
      // First check project limits
      const limitResponse = await getRequest('check_limits?type=project');

      if (!limitResponse.data.success) {
        if (
          user?.subscriptions?.current_subscription?.plan_name === 'Basic Plan'
        ) {
          setErrorMessage(
            limitResponse.data.message || 'You have reached your project limit',
          );
          setShowUpgradeModal(true);
          return;
        } else {
          setErrorMessage(
            limitResponse.data.message || 'You have reached your project limit',
          );
          setErrorLink(
            limitResponse.data.link ||
              'https://appsumo.com/products/vocable-ai/',
          );
          setShowErrorModal(true);
          return;
        }
      }

      // If limit check passes, proceed with campaign generation
      setIsGenerating(true);

      // Calculate start date (today) and end date (30 days from today)
      const today = new Date();
      const endDate = new Date();
      endDate.setDate(today.getDate() + 30);

      // Mapping of selected channel to content types (keys from SYSTEM_PROMPTS_HASH)
      const channelContentTypes = {
        Instagram: 'Instagram Carousel, Instagram Reel, Instagram Post',
        LinkedIn: 'Linkedin Post, Linkedin Article',
        Email: 'Email Newsletter',
        Blog: 'Blog Post, Guide, Case Study',
        YouTube: 'Youtube Script',
        Podcast: 'Podcast Episode',
        Twitter: 'Twitter Thread',
        Facebook: 'Facebook Post',
        TikTok: 'TikTok Video',
      };

      let contentTypes = '';

      // If no channels selected, use all content types
      if (selectedChannels.length === 0) {
        contentTypes = Object.values(channelContentTypes).join(', ');
      } else {
        // Create a combined content types string based on selected channels
        selectedChannels.forEach((channel) => {
          const channelContentType = channelContentTypes[channel] || '';
          if (channelContentType) {
            contentTypes += (contentTypes ? ', ' : '') + channelContentType;
          }
        });
      }

      // Debug logging to verify values
      console.log(
        'Selected Channels:',
        selectedChannels.length ? selectedChannels : 'All Channels',
      );
      console.log('Selected Brand ID:', selectedBrand);
      console.log('Brand Options:', brandOptions);
      const foundBrand = brandOptions.find((b) => b.value === selectedBrand);
      console.log('Found Brand for ID:', foundBrand);
      console.log('Selected Goal:', campaignGoal);
      console.log('Selected Language:', selectedLanguage);
      console.log('Computed Content Types:', contentTypes);

      // Check if contentTypes is blank and warn before proceeding
      if (!contentTypes) {
        console.error(
          'Content types are blank. Please check your selected channel or mapping.',
        );
        setIsGenerating(false);
        renderError('Please select at least one channel');
        return; // early exit to avoid creating a campaign with blank content types
      }

      try {
        // Get the brand name for the title generation
        const brandName =
          brandOptions.find((brand) => brand.value === selectedBrand)?.label ||
          '';

        // Generate campaign title using Claude
        let campaignTitle = 'New Campaign from Dashboard'; // Default fallback

        // Use description or extracted content for title generation
        const descriptionForTitle = campaignDescription ||
          (extractedSupportingContent ? `Based on uploaded documents: ${extractedSupportingContent.substring(0, 500)}` : '');

        try {
          const generatedTitle = await generateCampaignTitleWithClaude({
            description: descriptionForTitle,
            goal: campaignGoal || 'Engagement',
            channels: selectedChannels.length
              ? selectedChannels
              : Object.keys(channelContentTypes),
            brand: brandName,
            language: selectedLanguage,
          });

          if (generatedTitle) {
            campaignTitle = generatedTitle;
          } else {
            campaignTitle = createFallbackTitle(
              brandName,
              campaignGoal || 'Engagement',
              selectedChannels,
            );
          }
        } catch (titleError) {
          console.error('Title generation failed:', titleError);
          campaignTitle = createFallbackTitle(
            brandName,
            campaignGoal || 'Engagement',
            selectedChannels,
          );
        }

        // Prepare data with the generated title and append language instruction to description
        const languageInstruction =
          selectedLanguage !== 'en-US' && selectedLanguage !== 'en-GB'
            ? `\n\nPlease ensure the entire output is in ${languageLabel}.`
            : selectedLanguage === 'en-GB'
              ? `\n\nPlease ensure the entire output uses British English spelling and terminology.`
              : '';

        const data = {
          name: campaignTitle,
          description: campaignDescription + languageInstruction,
          campaign_goal: campaignGoal || 'Engagement',
          content_types: contentTypes,
          business_id: selectedBrand || '',
          content_no: 30,
          creation_type: 'ai_powered',
          start_date: today.toISOString(),
          end_date: endDate.toISOString(),
          briefs_qtt: 30,
          amount: 30,
          language: selectedLanguage,
          supporting_context: extractedSupportingContent || '',
        };

        const response = await createProject({ body: data });
        renderSuccess(response?.data?.message);
        navigate(
          `/projects/${response?.data?.project?.id}/newPlan?isManual=false`,
          {
            state: {
              project: response?.data?.project,
              campaignCards: true,
              amount: 30,
              customAmount: true,
            },
          },
        );
      } catch (error) {
        console.error('Error creating project:', error);
        renderError('Failed to create campaign. Please try again.');
      } finally {
        setIsGenerating(false);
      }
    } catch (error) {
      // console.error('Error checking limits:', error);
      // setErrorMessage(
      //   error.response?.data?.error || 'Failed to check project limits',
      // );
      // setShowErrorModal(true);
      if (
        user?.subscriptions?.current_subscription?.plan_name === 'Basic Plan'
      ) {
        setErrorMessage(
          error.response?.data?.error || 'Failed to check project limits',
        );
        setShowUpgradeModal(true);
        setIsGenerating(false);
      } else {
        setErrorMessage(
          error.response?.data?.error || 'Failed to check project limits',
        );
        setErrorLink('https://appsumo.com/products/vocable-ai/');
        setShowErrorModal(true);
        setIsGenerating(false);
      }
    }
  };

  // Handler for creating a new brief (unchanged from original)
  const onConfirmCreateHandler = async (payload) => {
    await createBrief({
      ...payload,
      deadline_date: payload.deadline_date
        ? formattedDate(payload.deadline_date)
        : '',
    });
    refetchBriefsCount();
  };

  return (
    <>
      {/* Keyframes for spinner animation */}
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <section className={Styles.Dashboard}>
        {/* New UI Elements */}
        <div
          style={{
            padding: '1.5rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '0.75rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          }}
        >
          {/* Greeting Section */}
          <div style={newUIStyles.greetingSection}>
            <h2 style={newUIStyles.greetingTitle}>
              Hey, {userName.split(' ')[0]}! What Are We Planning Today?{' '}
              <span style={{ color: '#FFD700' }}>✨</span>
            </h2>
            <p style={newUIStyles.greetingSubtitle}>
              Select one channel for 30 days of platform-specific content; or
              select multiple channels for a coordinated campaign.
            </p>
            {/* Channel Grid */}
            <div style={newUIStyles.channelGrid}>
              {channelOptions.map((channel) => (
                <div
                  key={channel.id}
                  style={{
                    ...newUIStyles.channelCard,
                    ...(selectedChannels.includes(channel.id)
                      ? {
                          ...newUIStyles.channelCardActive,
                          backgroundColor: channel.bgColor,
                        }
                      : {}),
                  }}
                  onClick={() => toggleChannelSelection(channel.id)}
                >
                  <channel.Icon
                    style={{
                      ...newUIStyles.channelIcon,
                      color: channel.color,
                    }}
                    size={24}
                  />
                  <span>{channel.id}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Clean White Prompt Box */}
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '1rem',
              padding: '1rem',
              marginBottom: '2rem',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              border: '1px solid #e5e7eb',
            }}
          >
            {/* File chips - shown when files are uploaded */}
            {supportingFiles.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  marginBottom: '0.75rem',
                }}
              >
                {supportingFiles.map((file, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '0.5rem',
                      padding: '0.35rem 0.6rem',
                      fontSize: '0.8rem',
                      color: '#374151',
                    }}
                  >
                    <FiFile size={14} />
                    <span
                      style={{
                        maxWidth: '120px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {file.name}
                    </span>
                    <FiX
                      size={14}
                      style={{ cursor: 'pointer', opacity: 0.6 }}
                      onClick={() => removeFile(index)}
                    />
                  </div>
                ))}
                {isExtractingContent && (
                  <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                    Extracting...
                  </span>
                )}
              </div>
            )}

            {/* Textarea */}
            <textarea
              placeholder="Describe your campaign goals, themes, and target audience..."
              value={campaignDescription}
              onChange={(e) => setCampaignDescription(e.target.value)}
              rows={2}
              style={{
                width: '100%',
                backgroundColor: 'transparent',
                border: 'none',
                outline: 'none',
                color: '#1f2937',
                fontSize: '0.95rem',
                fontFamily: 'inherit',
                resize: 'none',
                minHeight: '50px',
                maxHeight: '150px',
                padding: '0',
                lineHeight: '1.5',
              }}
              onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
              }}
            />

            {/* Bottom row with dropdowns and buttons */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: '0.75rem',
                gap: '0.5rem',
                flexWrap: 'wrap',
              }}
            >
              {/* Left side - Plus button and dropdowns */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                }}
              >
                {/* File upload button */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    padding: '0.4rem 0.75rem',
                    borderRadius: '1.5rem',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'transparent',
                    color: '#6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    cursor: 'pointer',
                    flexShrink: 0,
                    transition: 'all 0.2s ease',
                    fontSize: '0.85rem',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                    e.currentTarget.style.borderColor = '#9ca3af';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                  title="Upload supporting documents (PDF, DOCX, TXT, RTF)"
                >
                  <FiUpload size={14} />
                  <span>Upload Files</span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.txt,.rtf"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />

                {/* Campaign Goal Dropdown */}
                <div
                  ref={goalDropdownRef}
                  style={{
                    padding: '0.4rem 0.75rem',
                    background: 'transparent',
                    border: '1px solid #d1d5db',
                    color: '#374151',
                    fontSize: '0.85rem',
                    borderRadius: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => setShowGoalDropdown(!showGoalDropdown)}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#9ca3af';
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span>{campaignGoalLabel}</span>
                  <FiChevronDown size={12} />

                  {showGoalDropdown && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '0',
                        zIndex: '10',
                        backgroundColor: '#ffffff',
                        borderRadius: '0.5rem',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        minWidth: '160px',
                        marginBottom: '0.5rem',
                        border: '1px solid #e5e7eb',
                        overflow: 'hidden',
                      }}
                    >
                      {CAMPAIGN_GOALS.map((goal) => (
                        <div
                          key={goal.value}
                          style={{
                            padding: '0.6rem 0.75rem',
                            cursor: 'pointer',
                            color: '#374151',
                            backgroundColor:
                              campaignGoal === goal.value ? '#f3f4f6' : 'transparent',
                            fontSize: '0.85rem',
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#f3f4f6';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor =
                              campaignGoal === goal.value ? '#f3f4f6' : 'transparent';
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGoalSelect(goal);
                          }}
                        >
                          {goal.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Language Dropdown */}
                <div
                  ref={languageDropdownRef}
                  style={{
                    padding: '0.4rem 0.75rem',
                    background: 'transparent',
                    border: '1px solid #d1d5db',
                    color: '#374151',
                    fontSize: '0.85rem',
                    borderRadius: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#9ca3af';
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span>{languageLabel}</span>
                  <FiChevronDown size={12} />

                  {showLanguageDropdown && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '0',
                        zIndex: '10',
                        backgroundColor: '#ffffff',
                        borderRadius: '0.5rem',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        minWidth: '180px',
                        maxHeight: '250px',
                        overflowY: 'auto',
                        marginBottom: '0.5rem',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      {LANGUAGE_OPTIONS.map((language) => (
                        <div
                          key={language.value}
                          className="language-item"
                          style={{
                            padding: '0.6rem 0.75rem',
                            cursor: 'pointer',
                            color: '#374151',
                            backgroundColor:
                              selectedLanguage === language.value ? '#f3f4f6' : 'transparent',
                            fontSize: '0.85rem',
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#f3f4f6';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor =
                              selectedLanguage === language.value ? '#f3f4f6' : 'transparent';
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLanguageSelect(language);
                          }}
                        >
                          {language.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Brand Dropdown */}
                <div
                  ref={brandDropdownRef}
                  style={{
                    padding: '0.4rem 0.75rem',
                    background: 'transparent',
                    border: '1px solid #d1d5db',
                    color: '#374151',
                    fontSize: '0.85rem',
                    borderRadius: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => setShowBrandDropdown(!showBrandDropdown)}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#9ca3af';
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span>{brandLabel}</span>
                  <FiChevronDown size={12} />

                  {showBrandDropdown && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '0',
                        zIndex: '10',
                        backgroundColor: '#ffffff',
                        borderRadius: '0.5rem',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        minWidth: '180px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        marginBottom: '0.5rem',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      {getOrderedBrandOptions().length > 0 ? (
                        getOrderedBrandOptions().map((brand) => (
                          <div
                            key={brand.value}
                            style={{
                              padding: '0.6rem 0.75rem',
                              cursor: 'pointer',
                              color: '#374151',
                              backgroundColor:
                                selectedBrand === brand.value ? '#f3f4f6' : 'transparent',
                              fontSize: '0.85rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = '#f3f4f6';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor =
                                selectedBrand === brand.value ? '#f3f4f6' : 'transparent';
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBrandSelect(brand);
                            }}
                          >
                            {brand.label}
                            {selectedBrand === brand.value && (
                              <span
                                style={{
                                  fontSize: '0.7rem',
                                  color: '#9ca3af',
                                  marginLeft: '8px',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedBrand('');
                                  setBrandLabel('Choose brand');
                                  setShowBrandDropdown(false);
                                }}
                              >
                                clear
                              </span>
                            )}
                          </div>
                        ))
                      ) : (
                        <div
                          style={{
                            padding: '0.6rem 0.75rem',
                            color: '#9ca3af',
                            fontSize: '0.85rem',
                          }}
                        >
                          No brands available
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Right side - Send button */}
              <button
                onClick={handleGenerateCampaign}
                disabled={isGenerating || isCreateLoading}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor:
                    isGenerating || isCreateLoading ? '#e5e7eb' : '#111111',
                  color: isGenerating || isCreateLoading ? '#9ca3af' : '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor:
                    isGenerating || isCreateLoading ? 'not-allowed' : 'pointer',
                  flexShrink: 0,
                  transition: 'all 0.2s ease',
                }}
              >
                {isGenerating ? (
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #9ca3af',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                ) : (
                  <FiSend size={16} style={{ marginLeft: '2px' }} />
                )}
              </button>
            </div>

            {/* Error messages */}
            {supportingDocsErrors.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                {supportingDocsErrors.map((err, i) => (
                  <div
                    key={i}
                    style={{ color: '#ef4444', fontSize: '0.8rem' }}
                  >
                    {err}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Only keeping Active Campaigns section */}
        <ActiveCampaigns
          openProjectDrawer={
            planName === 'Basic Plan'
              ? () => setShowUpgradeModal(true)
              : () => {
                  // The campaign is now created directly from the prompt.
                }
          }
        />
      </section>

      {/* Bottom Actions - Removed as requested */}
      {modalView.newDocument && (
        <NewDocumentModal
          show={modalView.newDocument}
          onHide={() =>
            setModalView((prevState) => ({
              ...prevState,
              newDocument: false,
            }))
          }
        />
      )}

      {modalView.addTask && (
        <BriefDrawer
          show={modalView.addTask}
          onHide={() =>
            setModalView((prevState) => ({ ...prevState, addTask: false }))
          }
          action="create"
          onSubmit={(_, payload) => onConfirmCreateHandler(payload)}
        />
      )}

      {showErrorModal && (
        <CampaignErrorModal
          show={showErrorModal}
          onHide={() => setShowErrorModal(false)}
          errorMessage={errorMessage}
          errorLink={errorLink}
        />
      )}

      {showUpgradeModal && (
        <UpgradeModal
          show={showUpgradeModal}
          onHide={() => setShowUpgradeModal(false)}
          message={errorMessage}
        />
      )}
    </>
  );
};

export default Dashboard;
